import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MoreHorizontal, Building2, Calendar, DollarSign, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../ui/dropdown-menu';

const STAGES = [
  { id: 'new', label: 'Prospecto Nuevo' },
  { id: 'contacted', label: 'Llamada Realizada' },
  { id: 'meeting', label: 'Reunión Agendada' },
  { id: 'proposal', label: 'Propuesta Presentada' },
  { id: 'negotiation', label: 'Negociación' },
  { id: 'won', label: 'GANADO' },
  { id: 'lost', label: 'Perdido' }
];

export const LeadCard = ({ lead, isMobile, onMoveToStage, onClick, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id: lead.id,
    data: { ...lead },
    disabled: isMobile // Deshabilitar drag en móvil
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e) => {
    // Si se está arrastrando o se hizo click en el menú, no abrir detalle
    if (isDragging) return;
    if (onClick) onClick(lead.id);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (window.confirm("¿Estás seguro de eliminar este prospecto?")) {
      if (onDelete) onDelete(lead.id);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Helper para obtener color del badge basado en producto
  const getBadgeColor = (product) => {
    if (!product) return 'bg-slate-100 text-slate-700 border-slate-200';
    
    const p = product.toLowerCase();
    
    if (p.includes('vida')) return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    if (p.includes('gmm') || p.includes('salud') || p.includes('medicos')) return 'bg-emerald-100 text-emerald-800 border-emerald-200'; // Teal/Verde
    if (p.includes('auto')) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (p.includes('hogar')) return 'bg-amber-100 text-amber-800 border-amber-200';
    if (p.includes('daños')) return 'bg-red-50 text-red-700 border-red-200';
    
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // Cálculos de fecha relativa (simulada)
  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
      if (diffDays === 1) return "Ayer";
      if (diffDays === 0) return "Hoy";
      return `Hace ${diffDays}d`;
    } catch {
      return "";
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-manipulation" onClick={handleClick}>
      <Card className={`
        p-3 mb-3 bg-white hover:shadow-md transition-all border-slate-200 shadow-sm group cursor-pointer
        ${isDragging ? 'ring-2 ring-indigo-500 rotate-2' : ''}
      `}>
        {/* Header: Badge y Menú */}
        <div className="flex justify-between items-start mb-2">
          <Badge 
            variant="secondary" 
            className={`text-[10px] px-1.5 py-0.5 border ${getBadgeColor(lead.product)}`}
          >
            {lead.product || 'Sin producto'}
          </Badge>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Botón más grande en móvil para facilitar toque */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 -mr-2 text-slate-400 hover:text-indigo-600 data-[state=open]:text-indigo-600"
                // Evitar que el drag se active al tocar el botón
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Acciones</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onClick && onClick(lead.id)}>
                Ver Detalle Completo
              </DropdownMenuItem>
              
              {isMobile && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Mover a etapa...</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {STAGES.map(stage => (
                        <DropdownMenuItem 
                          key={stage.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onMoveToStage(lead.id, stage.id);
                          }}
                          disabled={lead.stage === stage.id}
                        >
                          {stage.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}
              
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600 focus:text-red-600 cursor-pointer"
                onClick={handleDeleteClick}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Info Principal */}
        <h4 className="font-bold text-slate-900 text-sm mb-1 leading-tight line-clamp-2">
          {lead.name}
        </h4>
        
        {lead.company && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
            <Building2 size={12} className="text-slate-400" />
            <span className="truncate max-w-[180px]">{lead.company}</span>
          </div>
        )}

        {/* Footer: Valor y Meta */}
        <div className="flex justify-between items-center pt-2.5 border-t border-slate-100 mt-2">
          <div className="flex items-center gap-1 text-slate-700 font-semibold text-sm">
            <DollarSign size={12} className="text-slate-400" />
            {formatCurrency(lead.value)}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
            <Calendar size={10} />
            {getRelativeTime(lead.updatedAt)}
          </div>
        </div>
      </Card>
    </div>
  );
};
